import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectOption } from '@/components/ui/select';
import { api } from '@/lib/api';
import { hasActiveLogFilters } from '@/lib/logFilter';
import type { APIKey } from '@/types/apiKey';
import type { ActorType, AuditActionInfo, LogFilters } from '@/types/auditLog';

interface LogFilterBarProps {
  mode: 'audit' | 'requests';
  filters: LogFilters;
  onChange: (filters: LogFilters) => void;
}

const toISO = (local: string) => (local ? new Date(local).toISOString() : undefined);
const toLocal = (iso?: string) => (iso ? iso.slice(0, 16) : '');

export function LogFilterBar({ mode, filters, onChange }: LogFilterBarProps) {
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [actions, setActions] = useState<AuditActionInfo[]>([]);
  const [search, setSearch] = useState(filters.q ?? '');

  useEffect(() => {
    api.apiKeys.list().then(setKeys).catch(() => setKeys([]));
    if (mode === 'audit') {
      api.auditLogs.meta().then((m) => setActions(m.actions)).catch(() => setActions([]));
    }
  }, [mode]);

  const set = (patch: Partial<LogFilters>) => onChange({ ...filters, ...patch, page: 1 });
  const resourceTypes = Array.from(new Set(actions.map((a) => a.resource_type))).sort();

  // Enter in any field submits the form, which applies the search text.
  const apply = (e: FormEvent) => {
    e.preventDefault();
    set({ q: search || undefined });
  };

  // Clears every filter and the local search text; the page size is kept.
  const clear = () => {
    setSearch('');
    onChange({ page: 1, limit: filters.limit });
  };
  const canClear = hasActiveLogFilters(filters) || search !== '';

  return (
    <form aria-label={mode === 'audit' ? 'Filter mutations' : 'Filter requests'} onSubmit={apply} className="space-y-2">
      <div className="grid gap-2 md:grid-cols-4 lg:grid-cols-8">
        <Select
          aria-label="API key"
          value={filters.api_key_id?.toString() ?? ''}
          onValueChange={(v) => set({ api_key_id: v ? Number(v) : undefined })}
        >
          <SelectOption value="">All API keys</SelectOption>
          {keys.map((k) => (
            <SelectOption key={k.id} value={k.id.toString()}>
              {k.name}
            </SelectOption>
          ))}
        </Select>
        <Select
          aria-label="Actor type"
          value={filters.actor_type ?? ''}
          onValueChange={(v) => set({ actor_type: (v || undefined) as ActorType | undefined })}
        >
          <SelectOption value="">All actors</SelectOption>
          <SelectOption value="api_key">API key</SelectOption>
          <SelectOption value="master">Master key</SelectOption>
          <SelectOption value="anonymous">Anonymous (rejected)</SelectOption>
        </Select>
        {mode === 'audit' ? (
          <>
            <Select aria-label="Action" value={filters.action ?? ''} onValueChange={(v) => set({ action: v || undefined })}>
              <SelectOption value="">All actions</SelectOption>
              {actions.map((a) => (
                <SelectOption key={a.action} value={a.action}>
                  {a.action}
                </SelectOption>
              ))}
            </Select>
            <Select aria-label="Resource type" value={filters.resource_type ?? ''} onValueChange={(v) => set({ resource_type: v || undefined })}>
              <SelectOption value="">All resources</SelectOption>
              {resourceTypes.map((r) => (
                <SelectOption key={r} value={r}>
                  {r}
                </SelectOption>
              ))}
            </Select>
            <Select
              aria-label="Result"
              value={filters.success === undefined ? '' : String(filters.success)}
              onValueChange={(v) => set({ success: v === '' ? undefined : v === 'true' })}
            >
              <SelectOption value="">Any result</SelectOption>
              <SelectOption value="true">Success</SelectOption>
              <SelectOption value="false">Failed</SelectOption>
            </Select>
          </>
        ) : (
          <Select aria-label="Method" value={filters.method ?? ''} onValueChange={(v) => set({ method: v || undefined })}>
            <SelectOption value="">All methods</SelectOption>
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
              <SelectOption key={m} value={m}>
                {m}
              </SelectOption>
            ))}
          </Select>
        )}
        <Input
          type="datetime-local"
          aria-label="From"
          value={toLocal(filters.from)}
          onChange={(e) => set({ from: toISO(e.target.value) })}
        />
        <Input type="datetime-local" aria-label="To" value={toLocal(filters.to)} onChange={(e) => set({ to: toISO(e.target.value) })} />
        <Input
          aria-label="Path or request ID"
          placeholder="Path or request ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onBlur={() => search !== (filters.q ?? '') && set({ q: search || undefined })}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" disabled={!canClear} onClick={clear}>
          Clear filters
        </Button>
        <Button type="submit" size="sm" variant="outline">
          Apply
        </Button>
      </div>
    </form>
  );
}
